import { appContext } from "../../../../AppContext";
import { IbssComponent } from "../../../../Components/Core/BaseComponent/IbssComponent";
import { PagedResponse } from "@iconics/core";
import { User } from "./DataModels";
import Helper from "../../../../Common/Helper";
import { JSX } from "react";
import { IRouterProps, withRouting } from "@iconics/components";
import { SerendieSymbol } from "@serendie/symbols";
import { longDebounceTimeInMilliseconds } from "@iconics/core";
import { Debouncer } from "@iconics/core";
import { Guid } from "@iconics/core";
import { Button, ButtonBar, Card, DataGrid, GridContainer, GridItem, IGridData, IGridRowsRequest, IGridRowsResponse, RightButtons, TextField, TickSymbol } from "@iconics/components";

class Users extends IbssComponent<IProps, IState>
{
    private deboucer = new Debouncer(longDebounceTimeInMilliseconds);

    constructor(props: IProps)
    {
        super(props);
        this.state = {
            canManageUsers: false,
            searchTerm: '',
            showAllUsers: false,
            gridData: { rows: [], checkedIds: [] },
            gridRefreshToken: '',
        };
    }

    public componentDidMount(): void
    {
        const { localStorageProvider } = appContext();
        this.setState({ canManageUsers: localStorageProvider.hasRight('API.Roles.ManageUsers') });
    }

    private handleSearchTermChange(event: React.ChangeEvent<HTMLInputElement>): void
    {
        this.setState({ searchTerm: event.target.value });
        this.deboucer.run(() => this.setState({ gridRefreshToken: Guid.new().toString() }));
    }

    private async handleRowsRequest(request: IGridRowsRequest<IUser>): Promise<IGridRowsResponse<IUser>>
    {
        const searchTerm = this.state.searchTerm.replace(/'/g, "''");
        let userResponse: PagedResponse<User[]>;
        let selectedIds: string[];

        if (this.state.showAllUsers)
        {
            userResponse = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.get<PagedResponse<User[]>>({
                select: User,
                tenancy: appContext().tenantId,
                skipToken: request.skipToken,
                top: request.take,
                filter: `contains(PreferredName, '${searchTerm}') or contains(Email, '${searchTerm}')`,
                orderBy: DataGrid.toODataOrderBy(request.sorter),
            });

            const userIds = userResponse.value.map(i => i.Id).map(i => `'${i}'`).join(',');

            const selectedResponse = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.users.get<PagedResponse<User[]>>({
                select: User,
                tenancy: appContext().tenantId,
                id: this.props.roleId,
                filter: `Id in (${userIds})`,
            });

            selectedIds = selectedResponse.value.map(i => i.Id);
        }
        else
        {
            userResponse = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.users.get<PagedResponse<User[]>>({
                select: User,
                tenancy: appContext().tenantId,
                id: this.props.roleId,
                skipToken: request.skipToken,
                top: request.take,
                filter: `contains(PreferredName, '${searchTerm}') or contains(Email, '${searchTerm}')`,
                orderBy: DataGrid.toODataOrderBy(request.sorter),
            });

            selectedIds = userResponse.value.map(i => i.Id);
        }

        const users = userResponse.value
            .map(user =>
            ({
                id: user.Id,
                email: user.Email,
                displayName: user.DisplayName,
                preferredName: user.PreferredName,
                userType: user.UserType,
                isAssigned: selectedIds.includes(user.Id),
            } as IUser));

        return { rows: users, nextSkipToken: userResponse.skipToken };
    }

    private async handleAssignUsers(): Promise<void>
    {
        this.loading(async () =>
        {
            const toAllocate = this.state.gridData.checkedIds ?? [];

            try
            {
                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.allocateUsers.post({
                    tenancy: appContext().tenantId,
                    id: this.props.roleId,
                    body: toAllocate.map(id => ({ Id: id })),
                });

                for (const id of toAllocate)
                {
                    this.setRow({ id: id, isAssigned: true });
                }
            }
            catch { }
        });
    }

    private async handleUnassignUsers(): Promise<void>
    {
        this.loading(async () =>
        {
            const toDeallocate = this.state.gridData.checkedIds ?? [];

            try
            {
                await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.roles.byId.deallocateUsers.delete({
                    tenancy: appContext().tenantId,
                    id: this.props.roleId,
                    body: toDeallocate,
                });

                for (const id of toDeallocate)
                {
                    this.setRow({ id: id, isAssigned: false });
                }
            }
            catch { }
        });
    }

    private setRow(row: Pick<IUser, "id"> & Partial<IUser>): void
    {
        this.setState(prevState => ({
            gridData:
            {
                ...prevState.gridData,
                rows: prevState.gridData.rows.map(r => r.id === row.id ? { ...r, ...row } : r),
            },
        }));
    }

    public render(): JSX.Element
    {
        const { labels } = appContext();

        return (
            <Card
                title={labels.Platform_Role_UserListTitle}
                subTitle={labels.Platform_Role_UserListSubTitle}
                icon={<SerendieSymbol name='apps-circle' />}
            >
                <GridContainer>
                    <GridItem expanded={3}>
                        <TextField
                            leftContent={<SerendieSymbol name={"magnifying-glass"} />}
                            placeholder={labels.Platform_CommonAction_Search}
                            value={this.state.searchTerm}
                            onChange={e => this.handleSearchTermChange(e)}
                        />
                    </GridItem>
                    {
                        this.props.roleUsesNativeSecurityProvider &&
                        <GridItem expanded={9}>
                            <ButtonBar>
                                <RightButtons
                                    fullWidth
                                >
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='close' />}
                                        onClick={() => this.handleUnassignUsers()}
                                        disabled={!this.state.canManageUsers || (this.state.gridData.checkedIds ?? []).length == 0}
                                    >
                                        {labels.Platform_Role_UnassignUser}
                                    </Button>
                                    <Button
                                        size='small'
                                        styleType='ghost'
                                        leftIcon={<SerendieSymbol name='check' />}
                                        onClick={() => this.handleAssignUsers()}
                                        disabled={!this.state.canManageUsers || (this.state.gridData.checkedIds ?? []).length == 0}
                                    >
                                        {labels.Platform_Role_AssignUser}
                                    </Button>
                                    {
                                        !this.state.showAllUsers &&
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='eye' />}
                                            onClick={() => this.setState({ showAllUsers: true, gridRefreshToken: Guid.new().toString() })}
                                        >
                                            {labels.Platform_Role_ShowAllUsers}
                                        </Button>
                                    }
                                    {
                                        this.state.showAllUsers &&
                                        <Button
                                            size='small'
                                            styleType='ghost'
                                            leftIcon={<SerendieSymbol name='eye-hidden' />}
                                            onClick={() => this.setState({ showAllUsers: false, gridRefreshToken: Guid.new().toString() })}
                                        >
                                            {labels.Platform_Role_HideAllUsers}
                                        </Button>
                                    }
                                </RightButtons>
                            </ButtonBar>
                        </GridItem>
                    }
                    <GridItem>
                        <DataGrid
                            columns={[
                                {
                                    columnId: Helper.nameOf<User>("DisplayName"),
                                    title: labels.User_DisplayName,
                                    getCellValue: i => i.displayName,
                                    columnWidth: 25,
                                },
                                {
                                    columnId: Helper.nameOf<User>("PreferredName"),
                                    title: labels.User_PreferredName,
                                    getCellValue: i => i.preferredName,
                                    columnWidth: 20,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<User>("Email"),
                                    title: labels.User_Email,
                                    getCellValue: i => i.email,
                                    columnWidth: 25,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<User>("UserType"),
                                    title: labels.User_UserType,
                                    getCellValue: i => i.userType,
                                    columnWidth: 20,
                                    sortable: true,
                                },
                                {
                                    columnId: Helper.nameOf<IUser>("isAssigned"),
                                    title: labels.User_IsAssignedToRole,
                                    getCellValue: i => i.isAssigned,
                                    template: i => <TickSymbol value={i.value as boolean} />,
                                    columnWidth: 10,
                                    sortable: false,
                                },
                            ]}
                            data={this.state.gridData}
                            onChange={i => this.setState({ gridData: i })}
                            rowId={i => i.id}
                            onRowsRequest={i => this.handleRowsRequest(i)}
                            onCellClicked={i => this.props.navigate(`/security-users/userdetails/${i.id}`)}
                            refreshToken={this.state.gridRefreshToken}
                            showCheckBoxes={this.props.roleUsesNativeSecurityProvider}
                            allowOnlyOneCheckBox={false}
                        />
                    </GridItem>
                </GridContainer>
            </Card>
        );
    }
}

export default withRouting<IRawProps>(Users);

export interface IProps extends IRawProps, IRouterProps { }

export interface IRawProps
{
    roleId: string;
    roleUsesNativeSecurityProvider: boolean;
}

export interface IState
{
    canManageUsers: boolean;
    searchTerm: string;
    showAllUsers: boolean;
    gridData: IGridData<IUser>;
    gridRefreshToken: string;
}

export interface IUser
{
    id: string;
    email: string;
    displayName: string;
    userType: string;
    preferredName: string;
    isAssigned: boolean;
    onClick: () => void;
}
